<script lang="ts" module>
  export type ButtonType = 'outline' | 'filled' | 'toggle';
  export type ButtonShape = 'rect' | 'circle' | 'icon';
</script>

<script lang="ts">
  import { isPresent } from '@yp/common/identity.ts';
  import type { Snippet } from 'svelte';
  import { DefaultScheme } from '../forms/common.ts';
  import { colorVar, type ColorScheme } from '../styles.ts';
  import { classes, styles } from './util.ts';

  interface Props {
    onclick?: (() => void) | undefined;
    class?: string | undefined;
    name?: string | undefined;
    color?: string | undefined;
    fgc?: string | undefined;
    scheme?: ColorScheme<string> | undefined;
    shape?: ButtonShape | undefined;
    type?: ButtonType | undefined;
    pressed?: boolean | undefined; // Only if type === 'toggle'
    role?: 'button' | 'submit' | 'reset' | undefined;
    // Whether this is a button in a button row after the first.
    // export let followOn = false;
    el?: HTMLButtonElement | undefined;
    disabled?: boolean | undefined;
    fakeDisabled?: boolean | undefined;
    children?: Snippet;
  }

  let {
    onclick = undefined,
    class: propClass = undefined,
    name = undefined,
    color = undefined,
    fgc = undefined,
    scheme = DefaultScheme,
    shape = 'rect',
    type = 'filled',
    pressed = false,
    role = 'button',
    el = $bindable(),
    disabled = undefined,
    fakeDisabled = undefined,
    children,
  }: Props = $props();

  const fg = $derived(isPresent(fgc) ? fgc : isPresent(color) ? '#fff' : colorVar(scheme, 'fg'));
  const bg = $derived(isPresent(color) ? color : colorVar(scheme, 'bg'));

  const clazz = $derived(
    classes(
      'button select-none inline-flex justify-center font-semibold shadow-xs',
      // 'text-sm'
      shape === 'rect'
        ? 'rounded-md px-3 py-2' // px-3 with text? // 'w-full sm:w-auto'
        : shape === 'circle'
          ? 'max-w-min max-h-min h-min rounded-full p-1.5'
          : 'rounded-sm px-1 py-1',
      type === 'filled' ? '' : `ring-2 ring-inset`,
      'enabled:hover:brightness-110 enabled:active:brightness-90',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
      'disabled:cursor-not-allowed disabled:!bg-gray-50 disabled:!text-gray-500 disabled:!ring-gray-200',
      (fakeDisabled ?? false)
        ? 'pointer-events-none cursor-not-allowed !bg-gray-50 !text-gray-500 !ring-gray-200'
        : undefined,
      // followOn ? 'mt-3 sm:mt-0 sm:ml-3' : '',
      // Overrides go last.
      propClass,
    ),
  );

  // Tailwind can only handle dynamic colors if they are symbolic names.
  const style = $derived(
    (propClass?.includes('btn-') ?? false)
      ? ''
      : styles(
          `color: ${type === 'filled' ? fg : bg}`,
          type === 'filled' ? `background-color: ${bg}` : `--tw-ring-color: ${bg}`,
          type === 'toggle' && pressed
            ? `background-color: ${colorVar(scheme, 'highlight')}`
            : undefined,
        ),
  );
</script>

<button {name} type={role} class={clazz} {style} {disabled} {onclick} bind:this={el}
  >{@render children?.()}</button>

<style lang="scss">
  button:not(:active) {
    -webkit-filter: drop-shadow-sm(2px 2px 2px rgba(0, 0, 0, 0.7));
    filter: drop-shadow-sm(2px 2px 2px rgba(0, 0, 0, 0.7));
  }
</style>
